import { useState } from "react";
import { ShieldCheck, Clock, XCircle, FileUp, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const readFile = (file) => new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file); });

const Status = ({ v }) => {
  if (v.status === "verified") return <div className="flex items-center gap-2 text-emerald-700 font-bold" data-testid="verification-verified"><ShieldCheck />حسابك موثّق — سجل تجاري {v.cr_number}</div>;
  if (v.status === "pending") return (
    <div className="space-y-1" data-testid="verification-pending">
      <div className="flex items-center gap-2 text-amber-700 font-bold"><Clock />طلب التوثيق قيد مراجعة الإدارة</div>
      <div className={`text-xs flex items-center gap-1 ${v.qr_match ? "text-emerald-700" : "text-red-600"}`}><QrCode size={14} />{v.qr_match ? "تمت قراءة الباركود ومطابقة رقم السجل" : v.qr_found ? "الباركود لا يطابق رقم السجل المدخل" : "لم نتمكن من قراءة الباركود في الملف"}</div>
    </div>
  );
  return null;
};

const Form = ({ onDone, rejected }) => {
  const { user } = useAuth();
  const [f, setF] = useState({ full_name: user.verification?.full_name || user.name || "", cr_number: user.verification?.cr_number || "", national_id: user.verification?.national_id || "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("رفع السجل التجاري إجباري");
    if (file.size > 5_000_000) return toast.error("الحد الأقصى للملف 5 ميجا");
    setBusy(true);
    try {
      const { data } = await api.post("/me/verification", { ...f, document: await readFile(file) });
      toast[data.qr_match ? "success" : "warning"](data.qr_match ? "تم التحقق من الباركود، بانتظار اعتماد الإدارة" : "تم الإرسال، لكن الباركود غير مطابق وستراجعه الإدارة يدوياً");
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="verification-form">
      {rejected && <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-xl p-3 text-sm" data-testid="verification-rejected"><XCircle size={16} />تم رفض طلب التوثيق{user.verification?.note ? `: ${user.verification.note}` : ""}. يمكنك إعادة الإرسال.</div>}
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className="text-xs font-semibold">الاسم الكامل *</label><Input data-testid="ver-name-input" required minLength={3} value={f.full_name} onChange={set("full_name")} className="h-11 rounded-xl mt-1" /></div>
        <div><label className="text-xs font-semibold">رقم السجل التجاري (10 أرقام) *</label><Input data-testid="ver-cr-input" required pattern="\d{10}" value={f.cr_number} onChange={set("cr_number")} dir="ltr" className="h-11 rounded-xl mt-1 text-right" /></div>
        <div><label className="text-xs font-semibold">رقم الهوية / الإقامة *</label><Input data-testid="ver-id-input" required pattern="[12]\d{9}" value={f.national_id} onChange={set("national_id")} dir="ltr" className="h-11 rounded-xl mt-1 text-right" /></div>
      </div>
      <label className="flex items-center gap-3 border-2 border-dashed rounded-xl p-4 cursor-pointer hover:border-brand transition-colors">
        <FileUp className="text-brand shrink-0" />
        <span className="text-sm">{file ? file.name : "ارفع صورة أو PDF للسجل التجاري متضمناً الباركود (QR) — إجباري"}</span>
        <input data-testid="ver-file-input" type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
      </label>
      <Button data-testid="ver-submit-btn" disabled={busy} type="submit" className="rounded-lg bg-brand hover:bg-brand-dark px-8">{busy ? "جارِ قراءة الباركود..." : "إرسال للتوثيق"}</Button>
    </form>
  );
};

export const VerificationCard = () => {
  const { user, refresh } = useAuth();
  if (user.is_admin || user.role !== "seller") return null;
  const v = user.verification;
  return (
    <div className="bg-white border-2 border-brand/20 rounded-xl p-5 space-y-4" data-testid="verification-card">
      <div>
        <h2 className="font-bold text-lg">توثيق الحساب</h2>
        {(!v || v.status === "rejected") && <p className="text-sm text-slate-500">التوثيق إجباري للبائعين لإضافة الإعلانات. نقرأ الباركود في السجل ونطابقه مع الرقم المدخل، ثم تعتمده الإدارة.</p>}
      </div>
      {v && v.status !== "rejected" ? <Status v={v} /> : <Form onDone={refresh} rejected={v?.status === "rejected"} />}
    </div>
  );
};
